import React, { useState, useEffect, useCallback, useMemo, useRef, useId } from 'react';
import {
  getCategories, createCategory, updateCategory, deleteCategory,
  getEvents, createEvent, updateEvent, deleteEvent,
} from '../../api/calendar';
import {
  Pin, CalendarDays, Calendar as CalendarIcon, Clock, Tag, FileText, Pencil, Trash2, ChevronLeft, ChevronRight, Plus, Check, X,
  BookOpen, Star, Bell, Users, Briefcase, CheckCircle, Flag, Heart, Zap, Target,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Dropdown from '../../components/Dropdown/Dropdown';
import DatePicker from '../../components/DatePicker/DatePicker';
import TimePicker from '../../components/TimePicker/TimePicker';
import SubTabs from '../../components/SubTabs/SubTabs';
import Modal from '../../components/Modal/Modal';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import PageHead from '../../components/PageHead/PageHead';
import { useConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import { useMediaQuery } from '../../components/Layout/shellState';
import { errorText } from '../../utils/errorText';
import { useI18n } from '../../i18n/I18nContext';
import {
  formatLongDate, formatDate, formatShortDate, formatMonthYear, formatTime as formatClock, weekdayShortLabels,
} from '../../utils/datetime';
import './Calendar.css';

// ── Constants ────────────────────────────────────────────────────────────────

const PRESET_COLORS = [
  '#FFC107', '#34C759', '#5856D6', '#FF6B35', '#30B0C7',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6B7280',
];
// What a screen reader says for a swatch (teamCalendar.calendar.colors.<name>), never the hex code
const COLOR_NAMES = {
  '#FFC107': 'yellow', '#34C759': 'green', '#5856D6': 'indigo', '#FF6B35': 'coral', '#30B0C7': 'teal',
  '#EC4899': 'pink', '#06B6D4': 'cyan', '#84CC16': 'lime', '#F97316': 'orange', '#6B7280': 'gray',
};

// The stored `iconName` of a category is one of these (the API keeps the name, not the glyph).
const ICONS = {
  BookOpen, Star, Bell, Calendar: CalendarIcon, Clock, Users, Briefcase, CheckCircle, Flag, Heart, Zap, Target,
};
const ICON_OPTIONS = Object.keys(ICONS);

const DEFAULT_EVENT = {
  title: '', date: '', time: '', endTime: '', description: '',
  color: PRESET_COLORS[0], pinned: false, categoryId: '',
};

const DEFAULT_CATEGORY = {
  name: '', color: PRESET_COLORS[1], bgColor: '', iconName: 'Star',
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const HOUR_PX = 52;
// An event with a start but no end is drawn as an hour long; no block is shorter than a line of text
const DEFAULT_MINUTES = 60;
const MIN_BLOCK_PX = 24;

// ── Helpers ──────────────────────────────────────────────────────────────────

const pad2 = (n) => String(n).padStart(2, '0');

function toDateStr(d) {
  // Returns YYYY-MM-DD for a Date object (local time)
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// Monday = 0 … Sunday = 6
const mondayIndex = (d) => (d.getDay() + 6) % 7;
const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

// The sheet shows the weeks the month needs (4 to 6 rows, the week starting on Monday), so there is
// no empty row of next month's days. The mini calendar asks for 6 so it never changes height.
function buildMonthGrid(year, month, weeks) {
  const lead = mondayIndex(new Date(year, month, 1));
  const rows = weeks || Math.ceil((lead + daysInMonth(year, month)) / 7);
  return Array.from({ length: rows * 7 }, (_, i) => new Date(year, month, 1 - lead + i));
}

function buildWeekDays(anchorDate) {
  // The 7 days (Monday first) of the week containing anchorDate
  const lead = mondayIndex(anchorDate);
  return Array.from({ length: 7 }, (_, i) => new Date(anchorDate.getFullYear(), anchorDate.getMonth(), anchorDate.getDate() - lead + i));
}

// "12–18-oktabr 2026" · "12–18 октября 2026" · "12–18 Oct 2026": a week inside one month names the month
// once; a week across two months is the full range ("28-sentabr – 4-oktabr 2026"). `short` abbreviates the
// months ("12–18-okt 2026") so the title shares a phone's first toolbar row with the arrows.
function weekTitle(days, lang, short = false) {
  const first = days[0];
  const last = days[days.length - 1];
  const named = (d, year) => (short ? formatShortDate(d, { lang, year }) : formatDate(d, { lang, year }));
  if (first.getMonth() !== last.getMonth() || first.getFullYear() !== last.getFullYear()) {
    const sameYear = first.getFullYear() === last.getFullYear();
    return `${named(first, sameYear ? 'never' : 'always')} – ${named(last, 'always')}`;
  }
  return named(last, 'always').replace(/^\d+/, `${first.getDate()}–${last.getDate()}`);
}

function colorToRgba(hex, alpha = 0.15) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// "9:5" is not a time; "10:00 - 12:00" (an old free-text value) starts at 10:00.
function normaliseTime(value) {
  const match = /(\d{1,2}):(\d{2})/.exec(String(value || ''));
  if (!match) return '';
  const h = Number(match[1]);
  const m = Number(match[2]);
  return h < 24 && m < 60 ? `${pad2(h)}:${pad2(m)}` : '';
}

// A month cell shows three events and "+N", so it lists the pinned ones first; the day list and the
// week sheet read in the order of the clock (an event with no time first, as "all day").
function eventsForDay(events, date, { pinnedFirst = false } = {}) {
  const key = toDateStr(date);
  const clock = (ev) => String(normaliseTime(ev.time) || '00:00');
  return events
    .filter((ev) => (ev.date ? ev.date.slice(0, 10) : '') === key)
    .sort((a, b) => (pinnedFirst ? (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) : 0) || clock(a).localeCompare(clock(b)));
}

// The API saves a category's name in capitals ("DARSLAR"). Every label in the CRM is sentence case, so
// a name that comes back all in capitals is shown as "Darslar"; a name with lower-case letters is left alone.
function categoryLabel(name) {
  const text = String(name || '').trim();
  if (!text || text !== text.toUpperCase() || text === text.toLowerCase()) return text;
  const lower = text.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
const readCategory = (cat) => ({ ...cat, name: categoryLabel(cat.name) });

const toMinutes = (hhmm) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm || '');
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

// Start and end of a timed event, in minutes from midnight. A missing or earlier end falls back to
// the default length (the API refuses an end before the start, but an old row may still hold one).
function eventSpan(ev) {
  const start = toMinutes(normaliseTime(ev.time));
  if (start == null) return null;
  const end = toMinutes(normaliseTime(ev.endTime));
  return { start, end: end != null && end > start ? end : Math.min(start + DEFAULT_MINUTES, 24 * 60), hasEnd: end != null && end > start };
}

// "09:30" or, when the event has an end, "09:30 – 10:30" (24-hour clock)
function timeRange(ev) {
  const start = normaliseTime(ev.time);
  const end = normaliseTime(ev.endTime);
  if (!start) return '';
  return end && toMinutes(end) > toMinutes(start) ? `${formatClock(start)} – ${formatClock(end)}` : formatClock(start);
}

// Side-by-side columns for events that overlap in time, so none hides another. Events are grouped
// into clusters (a run of overlapping events); inside a cluster each takes the first free column,
// and every event of the cluster is as wide as the cluster is deep.
function layoutDay(events) {
  const items = events
    .map((ev) => ({ ev, span: eventSpan(ev) }))
    .filter((item) => item.span)
    .sort((a, b) => a.span.start - b.span.start || b.span.end - a.span.end);
  const placed = [];
  let cluster = [];
  let columnEnds = [];
  let clusterEnd = -1;
  const flush = () => {
    cluster.forEach((item) => placed.push({ ...item, col: item.col, cols: columnEnds.length }));
    cluster = [];
    columnEnds = [];
  };
  items.forEach((item) => {
    if (item.span.start >= clusterEnd) flush();
    let col = columnEnds.findIndex((end) => end <= item.span.start);
    if (col === -1) col = columnEnds.length;
    columnEnds[col] = item.span.end;
    cluster.push({ ...item, col });
    clusterEnd = cluster.length === 1 ? item.span.end : Math.max(clusterEnd, item.span.end);
  });
  flush();
  return placed;
}

// An event paints itself in its own colour, as a soft tint with the dot in full colour.
const tint = (ev) => ({ '--ev': ev.color || 'var(--color-primary)' });

// Ink (dark or white) that reads on a swatch of this colour: the check mark of the chosen one.
function inkOn(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return '#FFFFFF';
  const channel = (i) => {
    const v = parseInt(m[1].slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  return luminance > 0.4 ? '#1C1C1E' : '#FFFFFF';
}

// The current moment, refreshed every minute: "today" must roll over at midnight on a page left open
// all night, and the week sheet draws a "now" line.
function useNow(intervalMs = 60000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function ColorPicker({ value, onChange, label }) {
  const { t } = useI18n();
  return (
    <div className="cal-swatches" role="group" aria-label={label}>
      {PRESET_COLORS.map((c) => {
        const name = t(`teamCalendar.calendar.colors.${COLOR_NAMES[c]}`);
        return (
          <button
            key={c}
            type="button"
            className={`cal-swatch hit-44${value === c ? ' selected' : ''}`}
            style={{ background: c, color: c, '--sw-ink': inkOn(c) }}
            onClick={() => onChange(c)}
            title={name}
            aria-label={name}
            aria-pressed={value === c}
          >
            {value === c && <Check aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
}

function MiniCalendar({ currentDate, today, onSelectDate }) {
  const { t, lang } = useI18n();
  const [miniDate, setMiniDate] = useState(() => new Date(currentDate));

  useEffect(() => { setMiniDate(new Date(currentDate)); }, [currentDate]);

  const cells = buildMonthGrid(miniDate.getFullYear(), miniDate.getMonth(), 6);
  const weekdays = weekdayShortLabels(lang);
  const todayStr = toDateStr(today);
  const selectedStr = toDateStr(currentDate);

  return (
    <div className="cal-mini">
      <div className="cal-mini-head">
        <button type="button" className="ui-icon-btn" onClick={() => setMiniDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))} aria-label={t('calendar.previous')}>
          <ChevronLeft aria-hidden="true" />
        </button>
        <span className="cal-mini-title" aria-live="polite">{formatMonthYear(miniDate.getFullYear(), miniDate.getMonth(), lang)}</span>
        <button type="button" className="ui-icon-btn" onClick={() => setMiniDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))} aria-label={t('calendar.next')}>
          <ChevronRight aria-hidden="true" />
        </button>
      </div>
      <div className="cal-mini-grid">
        {weekdays.map((d, i) => <span key={d} className={`cal-mini-dow${i >= 5 ? ' is-weekend' : ''}`} aria-hidden="true">{d}</span>)}
        {cells.map((cell) => {
          const str = toDateStr(cell);
          const isOther = cell.getMonth() !== miniDate.getMonth();
          const isToday = str === todayStr;
          const isSel = str === selectedStr;
          return (
            <button
              key={str}
              type="button"
              tabIndex={-1}
              className={`cal-mini-day${isOther ? ' other-month' : ''}${isToday ? ' today' : ''}${isSel && !isToday ? ' selected' : ''}${isWeekend(cell) ? ' is-weekend' : ''}`}
              aria-label={formatDate(cell, { lang })}
              aria-pressed={isSel}
              onClick={() => onSelectDate(new Date(cell))}
            >
              {cell.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CategoryModal({ initial, onSave, onClose, saving }) {
  const { t } = useI18n();
  const uid = useId();
  const [form, setForm] = useState(initial || DEFAULT_CATEGORY);
  const [touched, setTouched] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!form.name.trim()) return;
    onSave(form);
  };
  const nameMissing = touched && !form.name.trim();

  return (
    <Modal
      open
      onClose={onClose}
      title={initial?.id ? t('calendar.editCategory') : t('calendar.newCategory')}
      size="sm"
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" form={`${uid}-category`} className={`ds-btn ds-btn-primary${saving ? ' is-loading' : ''}`} disabled={saving}>
            {t('common.save')}
          </button>
        </>
      )}
    >
      <form id={`${uid}-category`} className="cal-form" onSubmit={handleSubmit} noValidate>
        <div className="ui-field">
          <label className="ui-label" htmlFor={`${uid}-name`}>{t('calendar.fName')} <span className="ui-req" aria-hidden="true">*</span></label>
          <input
            id={`${uid}-name`}
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder={t('calendar.placeholderCategoryName')}
            aria-invalid={nameMissing || undefined}
            autoComplete="off"
          />
          {nameMissing && <p className="ui-error">{t('teamCalendar.nameRequired')}</p>}
        </div>
        <div className="ui-field">
          <span className="ui-label">{t('calendar.fColor')}</span>
          <ColorPicker label={t('calendar.fColor')} value={form.color} onChange={(c) => setForm((f) => ({ ...f, color: c, bgColor: colorToRgba(c, 0.12) }))} />
        </div>
        <div className="ui-field">
          <span className="ui-label">{t('calendar.fIcon')}</span>
          <div className="cal-icons" role="group" aria-label={t('calendar.fIcon')}>
            {ICON_OPTIONS.map((name) => {
              const Icon = ICONS[name];
              const label = t(`teamCalendar.icons.${name}`);
              return (
                <button
                  key={name}
                  type="button"
                  className={`cal-icon-btn${form.iconName === name ? ' selected' : ''}`}
                  style={form.iconName === name ? { '--ev': form.color } : undefined}
                  title={label}
                  aria-label={label}
                  aria-pressed={form.iconName === name}
                  onClick={() => setForm((f) => ({ ...f, iconName: name }))}
                >
                  <Icon aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </div>
      </form>
    </Modal>
  );
}

function EventModal({ initial, categories, onSave, onDelete, onClose, saving }) {
  const { t } = useI18n();
  const uid = useId();
  const [form, setForm] = useState(() => (initial
    ? {
      ...DEFAULT_EVENT,
      ...initial,
      date: initial.date?.slice(0, 10) || '',
      time: normaliseTime(initial.time),
      endTime: normaliseTime(initial.endTime),
      description: initial.description || '',
      categoryId: initial.categoryId || '',
    }
    : { ...DEFAULT_EVENT }
  ));
  const [touched, setTouched] = useState(false);

  const isEdit = Boolean(initial?.id);
  const titleMissing = touched && !form.title.trim();
  const dateMissing = touched && !form.date;
  // The API refuses an end that is not after the start; say so before it has to
  let timeProblem = null;
  if (form.endTime && !form.time) timeProblem = 'endNeedsStart';
  else if (form.endTime && toMinutes(form.endTime) <= toMinutes(form.time)) timeProblem = 'endBeforeStart';

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!form.title.trim() || !form.date || timeProblem) return;
    // A new event leaves a field out when it is empty; an edit must say "none" (null), because a key
    // that is left out keeps the old value on the server (a cleared time would come back).
    const none = isEdit ? null : undefined;
    onSave({
      title: form.title.trim(),
      date: new Date(form.date).toISOString(),
      // The API takes a plain HH:mm; the TimePicker only ever gives that
      time: form.time || none,
      endTime: form.time && form.endTime ? form.endTime : none,
      description: form.description.trim() || none,
      color: form.color,
      pinned: form.pinned,
      categoryId: form.categoryId || none,
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? t('calendar.editEventTitle') : t('calendar.newEventTitle')}
      size="md"
      footer={(
        <>
          {isEdit && (
            <button type="button" className="ds-btn ds-btn-danger cal-modal-delete" onClick={() => onDelete(initial.id)}>
              {t('common.delete')}
            </button>
          )}
          <button type="button" className="ds-btn ds-btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" form={`${uid}-event`} className={`ds-btn ds-btn-primary${saving ? ' is-loading' : ''}`} disabled={saving}>
            {isEdit ? t('calendar.update') : t('calendar.create')}
          </button>
        </>
      )}
    >
      <form id={`${uid}-event`} className="cal-form" onSubmit={handleSubmit} noValidate>
        <div className="ui-field">
          <label className="ui-label" htmlFor={`${uid}-title`}>{t('calendar.fTitle')} <span className="ui-req" aria-hidden="true">*</span></label>
          <input
            id={`${uid}-title`}
            type="text"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder={t('calendar.placeholderEventTitle')}
            aria-invalid={titleMissing || undefined}
            autoComplete="off"
            data-autofocus
          />
          {titleMissing && <p className="ui-error">{t('teamCalendar.titleRequired')}</p>}
        </div>

        <div className="ui-field">
          <label className="ui-label" htmlFor={`${uid}-date`}>{t('calendar.fDate')} <span className="ui-req" aria-hidden="true">*</span></label>
          <DatePicker
            id={`${uid}-date`}
            value={form.date}
            onChange={(date) => setForm((f) => ({ ...f, date }))}
            aria-label={t('calendar.fDate')}
            invalid={dateMissing}
          />
          {dateMissing && <p className="ui-error">{t('teamCalendar.dateRequired')}</p>}
        </div>

        <div className="ui-picker-pair cal-time-pair">
          <div className="ui-field">
            <label className="ui-label" htmlFor={`${uid}-time`}>{t('teamCalendar.calendar.start')}</label>
            <TimePicker
              id={`${uid}-time`}
              value={form.time}
              onChange={(time) => setForm((f) => ({ ...f, time }))}
              aria-label={t('teamCalendar.calendar.start')}
              placeholder="--:--"
              minuteStep={5}
              allowClear
            />
          </div>
          <div className="ui-field">
            <label className="ui-label" htmlFor={`${uid}-end`}>{t('teamCalendar.calendar.end')}</label>
            <TimePicker
              id={`${uid}-end`}
              value={form.endTime}
              onChange={(endTime) => setForm((f) => ({ ...f, endTime }))}
              aria-label={t('teamCalendar.calendar.end')}
              placeholder="--:--"
              minuteStep={5}
              allowClear
              invalid={Boolean(timeProblem)}
            />
          </div>
          {timeProblem && <p className="ui-error is-wide">{t(`teamCalendar.calendar.${timeProblem}`)}</p>}
        </div>

        <div className="ui-field">
          <label className="ui-label" htmlFor={`${uid}-description`}>{t('calendar.fDescription')}</label>
          <textarea
            id={`${uid}-description`}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder={t('teamCalendar.calendar.placeholderDescription')}
          />
        </div>

        <div className="ui-field">
          <span className="ui-label">{t('calendar.fCategory')}</span>
          <Dropdown
            value={form.categoryId}
            onChange={(val) => setForm((f) => ({ ...f, categoryId: val }))}
            options={[
              { value: '', label: t('teamCalendar.calendar.noCategory'), separator: true },
              ...categories.map((c) => ({ value: c.id, label: c.name })),
            ]}
            placeholder={t('teamCalendar.calendar.noCategory')}
            ariaLabel={t('calendar.fCategory')}
          />
        </div>

        <div className="ui-field">
          <span className="ui-label">{t('calendar.fColor')}</span>
          <ColorPicker label={t('calendar.fColor')} value={form.color} onChange={(c) => setForm((f) => ({ ...f, color: c }))} />
        </div>

        <label className="ui-switch-row" htmlFor={`${uid}-pin`}>
          <span className="cal-pin-label"><Pin aria-hidden="true" /> {t('calendar.pinEvent')}</span>
          <input
            id={`${uid}-pin`}
            type="checkbox"
            role="switch"
            className="ui-switch"
            checked={form.pinned}
            onChange={(e) => setForm((f) => ({ ...f, pinned: e.target.checked }))}
          />
        </label>
      </form>
    </Modal>
  );
}

function EventDetail({ event, category, onEdit, onDelete, onClose }) {
  const { t, lang } = useI18n();
  return (
    <Modal
      open
      onClose={onClose}
      title={event.title}
      size="sm"
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn-danger cal-detail-delete" onClick={() => onDelete(event.id)}>
            <Trash2 aria-hidden="true" /> {t('common.delete')}
          </button>
          <button type="button" className="ds-btn ds-btn-primary" onClick={() => onEdit(event)}>
            <Pencil aria-hidden="true" /> {t('common.edit')}
          </button>
        </>
      )}
    >
      <div className="cal-detail" style={tint(event)}>
        <div className="cal-detail-row">
          <span className="cal-detail-ico"><CalendarIcon aria-hidden="true" /></span>
          <span>{formatLongDate(String(event.date).slice(0, 10), { lang, year: 'always' })}</span>
        </div>

        {event.time && (
          <div className="cal-detail-row">
            <span className="cal-detail-ico"><Clock aria-hidden="true" /></span>
            <span>{normaliseTime(event.time) ? timeRange(event) : event.time}</span>
          </div>
        )}

        {category && (
          <div className="cal-detail-row">
            <span className="cal-detail-ico"><Tag aria-hidden="true" /></span>
            <span className="cal-cat-pill" style={{ '--ev': category.color }}>{category.name}</span>
          </div>
        )}

        {event.description && (
          <div className="cal-detail-row">
            <span className="cal-detail-ico"><FileText aria-hidden="true" /></span>
            <span className="cal-detail-text">{event.description}</span>
          </div>
        )}

        {event.pinned && (
          <div className="cal-detail-row">
            <span className="cal-detail-ico"><Pin aria-hidden="true" /></span>
            <span>{t('calendar.pinned')}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Month View ────────────────────────────────────────────────────────────────

// One tab stop for the whole sheet (the selected day) and the arrow keys to move around it, as in a
// date picker; the events of the selected day are listed under the sheet, so the chips need no stop.
function MonthView({ currentDate, today, events, onSelect, onEventClick }) {
  const { t, lang } = useI18n();
  const gridRef = useRef(null);
  const focusDay = useRef(null);
  const cells = buildMonthGrid(currentDate.getFullYear(), currentDate.getMonth());
  const weekdays = weekdayShortLabels(lang);
  const todayStr = toDateStr(today);
  const selectedStr = toDateStr(currentDate);

  // After an arrow key the new day is on screen (perhaps in another month): put the focus on it
  useEffect(() => {
    if (!focusDay.current) return;
    const el = gridRef.current && gridRef.current.querySelector(`[data-day="${focusDay.current}"]`);
    focusDay.current = null;
    if (el) el.focus();
  });

  const onDayKeyDown = (e, cell) => {
    const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
    if (!step) return;
    e.preventDefault();
    const next = new Date(cell.getFullYear(), cell.getMonth(), cell.getDate() + step);
    focusDay.current = toDateStr(next);
    onSelect(next);
  };

  return (
    <div className="cal-month">
      <div className="cal-month-head">
        {weekdays.map((d, i) => <div key={d} className={`cal-dow${i >= 5 ? ' is-weekend' : ''}`}>{d}</div>)}
      </div>
      <div className="cal-grid" ref={gridRef} role="group" aria-label={formatMonthYear(currentDate.getFullYear(), currentDate.getMonth(), lang)}>
        {cells.map((cell) => {
          const cellStr = toDateStr(cell);
          const isOther = cell.getMonth() !== currentDate.getMonth();
          const isToday = cellStr === todayStr;
          const isSel = cellStr === selectedStr;
          const dayEvents = eventsForDay(events, cell, { pinnedFirst: true });

          return (
            <div
              key={cellStr}
              className={`cal-cell${isOther ? ' other-month' : ''}${isToday ? ' today' : ''}${isSel ? ' selected' : ''}${isWeekend(cell) ? ' is-weekend' : ''}`}
              onClick={() => onSelect(cell)}
            >
              <button
                type="button"
                className="cal-day-num"
                data-day={cellStr}
                tabIndex={isSel ? 0 : -1}
                aria-label={formatLongDate(cell, { lang, year: 'always' })}
                aria-pressed={isSel}
                aria-current={isToday ? 'date' : undefined}
                onClick={(e) => { e.stopPropagation(); onSelect(cell); }}
                onKeyDown={(e) => onDayKeyDown(e, cell)}
              >
                {cell.getDate()}
              </button>
              <div className="cal-events">
                {dayEvents.slice(0, 3).map((ev) => {
                  const time = normaliseTime(ev.time);
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      tabIndex={-1}
                      className="cal-chip"
                      style={tint(ev)}
                      onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
                      title={time ? `${timeRange(ev)} ${ev.title}` : ev.title}
                    >
                      {ev.pinned && <Pin className="cal-chip-pin" aria-hidden="true" />}
                      <span className="cal-chip-dot" aria-hidden="true" />
                      <span className="cal-chip-title">{ev.title}</span>
                    </button>
                  );
                })}
                {dayEvents.length > 3 && (
                  <button type="button" tabIndex={-1} className="cal-more" onClick={(e) => { e.stopPropagation(); onSelect(cell); }}>
                    {t('calendar.moreEvents', { count: dayEvents.length - 3 })}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Week View ─────────────────────────────────────────────────────────────────

function WeekView({ currentDate, today, now, events, onSelect, onEventClick }) {
  const { t, lang } = useI18n();
  const weekDays = buildWeekDays(currentDate);
  const weekdays = weekdayShortLabels(lang);
  const todayStr = toDateStr(today);
  const selectedStr = toDateStr(currentDate);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const bodyRef = useRef(null);

  // Open on the working day, not on midnight (a little above 07:00, so its label is not cut)
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 7 * HOUR_PX - 12;
  }, []);

  // An event with no time has no place on the hour grid: it sits in the all-day row above it
  const hasTime = (ev) => Boolean(normaliseTime(ev.time));
  const allDayByDay = weekDays.map((d) => eventsForDay(events, d).filter((ev) => !hasTime(ev)));
  const anyAllDay = allDayByDay.some((list) => list.length > 0);

  return (
    <div className="cal-week">
      <div className="cal-week-head">
        <div className="cal-time-gutter" />
        {weekDays.map((d, i) => {
          const str = toDateStr(d);
          return (
            <button
              key={str}
              type="button"
              className={`cal-week-day${str === todayStr ? ' today' : ''}${str === selectedStr ? ' selected' : ''}${i >= 5 ? ' is-weekend' : ''}`}
              aria-pressed={str === selectedStr}
              aria-label={formatLongDate(d, { lang, year: 'always' })}
              onClick={() => onSelect(d)}
            >
              <span className="cal-week-dow">{weekdays[i]}</span>
              <span className="cal-week-num">{d.getDate()}</span>
            </button>
          );
        })}
      </div>

      {anyAllDay && (
        <div className="cal-week-allday">
          <div className="cal-allday-label">{t('teamCalendar.allDay')}</div>
          {weekDays.map((d, i) => (
            <div key={toDateStr(d)} className="cal-allday-cell">
              {allDayByDay[i].map((ev) => (
                <button key={ev.id} type="button" className="cal-chip" style={tint(ev)} onClick={() => onEventClick(ev)} title={ev.title}>
                  {ev.pinned ? <Pin className="cal-chip-pin" aria-hidden="true" /> : <span className="cal-chip-dot" aria-hidden="true" />}
                  <span className="cal-chip-title">{ev.title}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="cal-week-body" ref={bodyRef}>
        {/* Time gutter: 01:00 … 23:00 */}
        <div className="cal-time-col">
          {HOURS.map((h) => (
            <div key={h} className="cal-time-slot">
              {h > 0 && <span className="cal-time-label">{formatClock(`${h}:00`)}</span>}
            </div>
          ))}
        </div>

        {/* Day columns */}
        {weekDays.map((d) => {
          const str = toDateStr(d);
          const isToday = str === todayStr;
          return (
            <div
              key={str}
              className={`cal-day-col${isToday ? ' today' : ''}`}
              onClick={() => onSelect(d)}
            >
              {HOURS.map((h) => <div key={h} className="cal-hour-slot" />)}
              <div className="cal-events-layer">
                {layoutDay(eventsForDay(events, d)).map(({ ev, span, col, cols }) => {
                  const height = Math.max(MIN_BLOCK_PX, ((span.end - span.start) / 60) * HOUR_PX - 2);
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      className={`cal-block${height < 36 ? ' is-short' : ''}`}
                      style={{ ...tint(ev), top: (span.start / 60) * HOUR_PX, height, '--col': col, '--cols': cols }}
                      onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
                      title={`${timeRange(ev)} ${ev.title}`}
                    >
                      <span className="cal-block-title">
                        {ev.pinned && <Pin className="cal-chip-pin" aria-hidden="true" />}{ev.title}
                      </span>
                      <span className="cal-block-time">{timeRange(ev)}</span>
                    </button>
                  );
                })}
                {isToday && (
                  <div className="cal-now" style={{ top: (nowMinutes / 60) * HOUR_PX }} aria-hidden="true" title={t('teamCalendar.calendar.nowLabel')} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── A day, as a list ──────────────────────────────────────────────────────────

// The rows of a day: the time (or "all day"), the colour dot, the title with its category and note, a
// chevron. The selected-day list under the sheet and the phone's week list draw the same rows.
function AgendaRows({ events, categories, onEventClick }) {
  const { t } = useI18n();
  return (
    <div className="ui-list--feed">
      {events.map((ev) => {
        const category = categories.find((c) => c.id === ev.categoryId);
        const time = normaliseTime(ev.time);
        return (
          <button key={ev.id} type="button" className="ui-list-row cal-agenda-row" style={tint(ev)} onClick={() => onEventClick(ev)}>
            <span className="cal-agenda-time">{time ? formatClock(time) : t('teamCalendar.allDay')}</span>
            <span className="cal-chip-dot" aria-hidden="true" />
            <span className="ui-list-row__body">
              <span className="ui-list-row__title">{ev.pinned && <Pin className="cal-chip-pin" aria-hidden="true" />}{ev.title}</span>
              {(category || ev.description) && (
                <span className="ui-list-row__sub">{[category?.name, ev.description].filter(Boolean).join(' · ')}</span>
              )}
            </span>
            <ChevronRight className="ui-list-row__chevron" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}

// The selected day, under the sheet. While the events are still on their way an empty day is not yet
// "nothing planned": a placeholder row stands in, so the list does not claim an empty day and then fill up.
function DayAgenda({ date, today, events, categories, loading, onEventClick, onAdd }) {
  const { t, lang } = useI18n();
  const dayEvents = eventsForDay(events, date);
  const isToday = toDateStr(date) === toDateStr(today);

  return (
    <section className="cal-agenda" aria-label={formatLongDate(date, { lang, year: 'always' })} aria-busy={loading || undefined}>
      <div className="cal-agenda-head">
        <div>
          {isToday && <p className="cal-agenda-eyebrow">{t('calendar.today')}</p>}
          <h3 className="cal-agenda-title">{formatLongDate(date, { lang })}</h3>
        </div>
        <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm hit-44" onClick={onAdd}>
          <Plus aria-hidden="true" /> {t('teamCalendar.addEvent')}
        </button>
      </div>

      {dayEvents.length === 0 ? (
        loading ? (
          <div className="ui-skel cal-agenda-skel" aria-hidden="true" />
        ) : (
          <InlineEmpty
            kind="calendar"
            title={t('teamCalendar.dayEmptyTitle')}
            description={t('teamCalendar.dayEmptyDesc')}
          />
        )
      ) : (
        <AgendaRows events={dayEvents} categories={categories} onEventClick={onEventClick} />
      )}
    </section>
  );
}

// A phone has no room for seven hour columns: its week is the seven days one under the other, each with
// its events (the rows of the day list) and a "+" that adds one on that day. A quiet line stands in for
// a day with nothing planned, so a week does not become seven empty boxes.
function WeekAgenda({ currentDate, today, events, categories, loading, onEventClick, onAdd }) {
  const { t, lang } = useI18n();
  const todayStr = toDateStr(today);

  return (
    <div className="cal-wk">
      {buildWeekDays(currentDate).map((d) => {
        const key = toDateStr(d);
        const isToday = key === todayStr;
        const dayEvents = eventsForDay(events, d);
        const title = formatLongDate(d, { lang });
        return (
          <section key={key} className={`cal-wk-day${isToday ? ' today' : ''}`} aria-label={formatLongDate(d, { lang, year: 'always' })}>
            <div className="cal-wk-head">
              <h3 className="cal-wk-title">{title}</h3>
              {isToday && <span className="ds-badge ds-badge-brand">{t('calendar.today')}</span>}
              <button
                type="button"
                className="ui-icon-btn cal-wk-add"
                onClick={() => onAdd(d)}
                aria-label={t('teamCalendar.addEventOn', { date: title })}
                title={t('teamCalendar.addEvent')}
              >
                <Plus aria-hidden="true" />
              </button>
            </div>
            {dayEvents.length === 0
              ? (loading
                ? <div className="ui-skel cal-wk-skel" aria-hidden="true" />
                : <p className="cal-wk-none">{t('teamCalendar.weekEmpty')}</p>)
              : <AgendaRows events={dayEvents} categories={categories} onEventClick={onEventClick} />}
          </section>
        );
      })}
    </div>
  );
}

// ── Main Calendar Component ──────────────────────────────────────────────────

export default function Calendar() {
  const { t, lang } = useI18n();
  const now = useNow();
  // "Today" follows the clock: a page left open past midnight moves its yellow chip to the new day
  const todayKey = toDateStr(now);
  const today = useMemo(() => {
    const [y, m, d] = todayKey.split('-').map(Number);
    return new Date(y, m - 1, d);
  }, [todayKey]);
  const [confirm, confirmDialog] = useConfirmDialog();

  const [currentDate, setCurrentDate] = useState(() => new Date(today));
  const [view, setView] = useState('month');
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null); // filter
  // A phone has no room for seven hour columns: its week is a list of the seven days (WeekAgenda)
  const narrow = useMediaQuery('(max-width: 639px)', false);

  // Modals
  const [eventModal, setEventModal] = useState(null); // null | { mode:'create'|'edit', event }
  const [detailPanel, setDetailPanel] = useState(null); // null | event
  const [categoryModal, setCategoryModal] = useState(null); // null | { category }
  const [saving, setSaving] = useState(false);

  const activeView = view;
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      // Fetch events for ±1 month so week-view edge cases work (to the very end of the last day)
      const start = new Date(year, month - 1, 1).toISOString();
      const end = new Date(year, month + 2, 0, 23, 59, 59, 999).toISOString();

      const params = { start, end };
      if (selectedCategory) params.categoryId = selectedCategory;

      const [evs, cats] = await Promise.all([getEvents(params), getCategories()]);
      setEvents(evs);
      setCategories(cats.map(readCategory));
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [year, month, selectedCategory]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Navigation ─────────────────────────────────────────────────────────────

  function navigate(dir) {
    setCurrentDate((d) => {
      if (activeView === 'month') return new Date(d.getFullYear(), d.getMonth() + dir, 1);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate() + dir * 7);
    });
  }

  function goToday() { setCurrentDate(new Date(today)); }
  function selectDay(date) { setCurrentDate(startOfDay(date)); }

  function getTitle() {
    if (activeView === 'month') return formatMonthYear(year, month, lang);
    return weekTitle(buildWeekDays(currentDate), lang, narrow);
  }

  // ── Event actions ──────────────────────────────────────────────────────────

  function openCreateEvent(date) {
    setDetailPanel(null);
    setEventModal({
      mode: 'create',
      event: { ...DEFAULT_EVENT, date: toDateStr(date) },
    });
  }

  function openEditEvent(ev) {
    setDetailPanel(null);
    setEventModal({ mode: 'edit', event: ev });
  }

  async function handleSaveEvent(payload) {
    setSaving(true);
    try {
      if (eventModal.mode === 'edit') {
        const updated = await updateEvent(eventModal.event.id, payload);
        setEvents((evs) => evs.map((e) => (e.id === updated.id ? updated : e)));
      } else {
        const created = await createEvent(payload);
        setEvents((evs) => [...evs, created]);
      }
      setEventModal(null);
    } catch (err) {
      toast.error(errorText(err, t('calendar.failedToSaveEvent')));
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteEvent(id) {
    const ok = await confirm({
      title: t('calendar.confirmDeleteEvent'),
      description: t('teamCalendar.deleteEventNote'),
      confirmLabel: t('common.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    setSaving(true);
    try {
      await deleteEvent(id);
      setEvents((evs) => evs.filter((e) => e.id !== id));
      setEventModal(null);
      setDetailPanel(null);
    } catch (err) {
      toast.error(errorText(err, t('calendar.failedToDeleteEvent')));
    } finally {
      setSaving(false);
    }
  }

  function handleEventClick(ev) {
    setEventModal(null);
    setDetailPanel(ev);
  }

  // ── Category actions ───────────────────────────────────────────────────────

  async function handleSaveCategory(form) {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        color: form.color,
        bgColor: colorToRgba(form.color, 0.12),
        iconName: form.iconName,
      };
      if (categoryModal.category?.id) {
        const updated = readCategory(await updateCategory(categoryModal.category.id, payload));
        setCategories((cs) => cs.map((c) => (c.id === updated.id ? updated : c)));
      } else {
        const created = readCategory(await createCategory(payload));
        setCategories((cs) => [...cs, created]);
      }
      setCategoryModal(null);
    } catch (err) {
      toast.error(errorText(err, t('calendar.failedToSaveCategory')));
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteCategory(id) {
    const ok = await confirm({
      title: t('teamCalendar.deleteCategoryTitle'),
      description: t('teamCalendar.deleteCategoryNote'),
      confirmLabel: t('common.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await deleteCategory(id);
      setCategories((cs) => cs.filter((c) => c.id !== id));
      if (selectedCategory === id) setSelectedCategory(null);
    } catch (err) {
      toast.error(errorText(err, t('calendar.failedToDeleteCategory')));
    }
  }

  // ── Pinned events (across all loaded events) ───────────────────────────────

  const pinnedEvents = events.filter((e) => e.pinned).slice(0, 8);
  const activeCategory = selectedCategory ? categories.find((c) => c.id === selectedCategory) : null;

  // The sheet: the month grid, the week's hour grid, or (a phone's week) the seven days as a list
  const weekList = narrow && activeView === 'week';
  let sheet;
  if (activeView === 'month') {
    sheet = <MonthView currentDate={currentDate} today={today} events={events} onSelect={selectDay} onEventClick={handleEventClick} />;
  } else if (weekList) {
    sheet = (
      <WeekAgenda
        currentDate={currentDate}
        today={today}
        events={events}
        categories={categories}
        loading={loading}
        onEventClick={handleEventClick}
        onAdd={openCreateEvent}
      />
    );
  } else {
    sheet = <WeekView currentDate={currentDate} today={today} now={now} events={events} onSelect={selectDay} onEventClick={handleEventClick} />;
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="cal-page">
      <PageHead
        icon={<CalendarDays />}
        title={t('nav.calendar')}
        description={t('teamCalendar.subtitle')}
        actions={(
          <button type="button" className="ds-btn ds-btn-primary" onClick={() => openCreateEvent(currentDate)}>
            <Plus aria-hidden="true" /> {t('calendar.newEvent')}
          </button>
        )}
      />

      <div className="page-grid page-grid--rail">
        {/* Main */}
        <section className="cal-main section-card">
          {/* Toolbar */}
          <div className="cal-toolbar">
            <div className="cal-toolbar-nav">
              <button type="button" className="ds-btn ds-btn-secondary ds-btn-icon ds-btn-sm" onClick={() => navigate(-1)} aria-label={t('calendar.previous')}><ChevronLeft aria-hidden="true" /></button>
              <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={goToday}>{t('calendar.today')}</button>
              <button type="button" className="ds-btn ds-btn-secondary ds-btn-icon ds-btn-sm" onClick={() => navigate(1)} aria-label={t('calendar.next')}><ChevronRight aria-hidden="true" /></button>
            </div>
            <h2 className="cal-toolbar-title" aria-live="polite">{getTitle()}</h2>
            <SubTabs
              className="cal-view-tabs"
              ariaLabel={t('teamCalendar.viewAria')}
              active={view}
              onChange={setView}
              tabs={[
                { key: 'month', label: t('calendar.month') },
                { key: 'week', label: t('calendar.week') },
              ]}
            />
          </div>

          {/* The category in force. The rail (and its list of categories) sits far below the sheet on a
              phone or tablet, so the filter is told here, with a way to drop it */}
          {activeCategory && (
            <div className="cal-filter">
              <span className="ui-chip ui-chip--removable" style={tint({ color: activeCategory.color })}>
                <span className="cal-chip-dot" aria-hidden="true" />
                <span className="cal-filter-name">{activeCategory.name}</span>
                <button
                  type="button"
                  className="ui-chip__x"
                  onClick={() => setSelectedCategory(null)}
                  aria-label={t('teamCalendar.removeFilter', { name: activeCategory.name })}
                >
                  <X aria-hidden="true" />
                </button>
              </span>
            </div>
          )}

          {/* Pinned band */}
          {pinnedEvents.length > 0 && (
            <div className="cal-pinned-band">
              <span className="cal-pinned-label"><Pin aria-hidden="true" /> {t('calendar.pinned')}</span>
              {pinnedEvents.map((ev) => (
                <button
                  key={ev.id}
                  type="button"
                  className="cal-pinned-pill"
                  style={tint(ev)}
                  onClick={() => handleEventClick(ev)}
                  title={formatDate(String(ev.date).slice(0, 10), { lang })}
                >
                  <span className="cal-chip-dot" aria-hidden="true" />
                  <span className="cal-chip-title">{ev.title}</span>
                </button>
              ))}
            </div>
          )}

          {/* Calendar body */}
          {error ? (
            <div className="cal-state">
              <PageEmptyState variant="error" size="lg" onRetry={fetchAll} />
            </div>
          ) : (
            <div className={`cal-body${loading ? ' cal-busy' : ''}`} aria-busy={loading || undefined}>
              {sheet}
            </div>
          )}

          {/* When the events could not be loaded, "nothing planned" would be a lie: say nothing. The phone's
              week already lists every day, so it needs no list of the selected one under it */}
          {!error && !weekList && (
            <DayAgenda
              date={currentDate}
              today={today}
              events={events}
              categories={categories}
              loading={loading}
              onEventClick={handleEventClick}
              onAdd={() => openCreateEvent(currentDate)}
            />
          )}
        </section>

        {/* Rail: mini calendar + categories */}
        <aside className="col-rail cal-rail">
          <section className="section-card cal-mini-card">
            <MiniCalendar currentDate={currentDate} today={today} onSelectDate={selectDay} />
          </section>

          <section className="section-card" aria-label={t('calendar.categories')}>
            <div className="section-head">
              <h2 className="section-head__title">{t('calendar.categories')}</h2>
              <button type="button" className="section-head__action" onClick={() => setCategoryModal({ category: null })}>
                + {t('common.add')}
              </button>
            </div>
            <div className="cal-cats">
              <div className={`cal-cat${!selectedCategory ? ' active' : ''}`}>
                <button type="button" className="cal-cat-main" aria-pressed={!selectedCategory} onClick={() => setSelectedCategory(null)}>
                  <span className="ui-icon-chip ui-icon-chip--sm" data-tone="neutral"><CalendarIcon aria-hidden="true" /></span>
                  <span className="cal-cat-name">{t('calendar.allEvents')}</span>
                </button>
              </div>
              {categories.map((cat) => {
                const Icon = ICONS[cat.iconName] || Tag;
                return (
                  <div key={cat.id} className={`cal-cat${selectedCategory === cat.id ? ' active' : ''}`}>
                    <button
                      type="button"
                      className="cal-cat-main"
                      aria-pressed={selectedCategory === cat.id}
                      onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
                    >
                      <span className="ui-icon-chip ui-icon-chip--sm cal-cat-chip" style={{ '--ev': cat.color }}><Icon aria-hidden="true" /></span>
                      <span className="cal-cat-name" title={cat.name}>{cat.name}</span>
                    </button>
                    {/* One "⋯" instead of two icons: the name keeps ~40px more room in the narrow rail */}
                    <ActionMenu
                      className="cal-cat-actions"
                      ariaLabel={`${t('teamCalendar.categoryActions')}: ${cat.name}`}
                      items={[
                        { key: 'edit', label: t('common.edit'), onSelect: () => setCategoryModal({ category: cat }) },
                        { key: 'delete', label: t('common.delete'), tone: 'danger', onSelect: () => handleDeleteCategory(cat.id) },
                      ]}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        </aside>
      </div>

      {/* Event detail. While a question about it is open it steps aside (two panels of different sizes
          stacked look like a glitch); the question's "no" brings it back */}
      {detailPanel && !eventModal && !confirmDialog && (
        <EventDetail
          event={detailPanel}
          category={categories.find((c) => c.id === detailPanel.categoryId)}
          onEdit={openEditEvent}
          onDelete={handleDeleteEvent}
          onClose={() => setDetailPanel(null)}
        />
      )}

      {/* Event create/edit modal */}
      {eventModal && (
        <EventModal
          initial={eventModal.event}
          categories={categories}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
          onClose={() => setEventModal(null)}
          saving={saving}
        />
      )}

      {/* Category modal */}
      {categoryModal && (
        <CategoryModal
          initial={categoryModal.category}
          onSave={handleSaveCategory}
          onClose={() => setCategoryModal(null)}
          saving={saving}
        />
      )}

      {confirmDialog}
    </div>
  );
}
